/**
 * PageNarration — Auto-play-on-entry narration surface (headless).
 *
 * May 01 2026 per Lead's explicit directive:
 *   "You have narration buttons that dont play anything. Every page is
 *    supposed to narrated automatically when user enter a page. If you
 *    cant get it to play remove button for now."
 *
 * Behaviour:
 *   1. On mount, request the page MP3 from `/api/narration/page/{pageKey}`.
 *   2. Attempt `audio.play()` immediately — if Chrome/Firefox's autoplay
 *      policy allows it (user has a prior gesture on this origin), the
 *      narration starts. No UI is shown.
 *   3. If the first play() is rejected (hard autoplay block on a cold
 *      landing with zero gestures), we attach a ONE-SHOT document-level
 *      click / keydown / touchstart listener that retries play() on the
 *      next user gesture anywhere on the page. Still no UI — the moment
 *      the user clicks anything (a tab, a button, the globe), narration
 *      begins silently.
 *   4. The button is never rendered. Per Lead: "If you cant get it to
 *      play remove button for now." We removed the button.
 *
 * The audio element unmounts with the component, so navigating tabs
 * correctly stops the previous page's narration. A tiny `pageKey` change
 * tears down the current audio and re-fires the whole flow for the new
 * page.
 */
import { useEffect, useRef } from 'react';
import api from '../lib/api';

import { apiBase } from '../lib/absoluteMediaUrl';

// Audio <src> must use the resolved API base, not a relative path or a
// stale env var. See `lib/absoluteMediaUrl.js` for the cross-origin
// rationale (production custom domain vs Emergent preview).
const API_BASE = apiBase();

export default function PageNarration({ pageKey }) {
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const audio = new Audio();
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    audioRef.current = audio;

    let gestureHandler = null;
    const cleanupGesture = () => {
      if (!gestureHandler) return;
      ['pointerdown', 'keydown', 'touchstart'].forEach((ev) => {
        document.removeEventListener(ev, gestureHandler, true);
      });
      gestureHandler = null;
    };

    const fireAndPlay = async () => {
      try {
        const r = await api.get(`/narration/page/${pageKey}`);
        if (cancelled) return;
        const fileId = r.data?.id;
        if (!fileId) return;
        audio.src = `${API_BASE}/api/media/file/${fileId}`;

        // First attempt — works if the user has a prior gesture.
        try {
          await audio.play();
          return;
        } catch (e) {
          // Autoplay blocked. Fall through to gesture-retry.
        }
        if (cancelled) return;
        gestureHandler = () => {
          cleanupGesture();
          audio.play().catch((e) => {
            // User denied once more; log but stay silent in the UI.
            console.warn('[PageNarration] gesture-retry play failed', e);
          });
        };
        ['pointerdown', 'keydown', 'touchstart'].forEach((ev) => {
          document.addEventListener(ev, gestureHandler, { capture: true, once: true, passive: true });
        });
      } catch (e) {
        // Bake failed (5xx, network). Silent per Lead's brief — no red
        // error tag, no retry button. The page is still fully readable.
        console.warn('[PageNarration] bake failed for', pageKey, e);
      }
    };

    fireAndPlay();

    return () => {
      cancelled = true;
      cleanupGesture();
      try { audio.pause(); } catch { /* ignore */ }
      audio.src = '';
      audioRef.current = null;
    };
  }, [pageKey]);

  // Intentionally render nothing — per Lead's directive the visible button
  // has been removed. Narration is headless / auto.
  return null;
}
